import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesomeOutlined,
  Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import AISeasonalChart from '../AISeasonalChart';
import InsightCard from './InsightCard';
import InsightDetailDialog from './InsightDetailDialog';
import InsightsQualityPanel from './InsightsQualityPanel';
import CopilotPanel from './CopilotPanel';
import MorningBriefPanel from './MorningBriefPanel';
import { normalizeInsight, normalizeInsightsResponse } from './insightUtils';

const InsightsCenter = ({ compact = false }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [insights, setInsights] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [generatedAt, setGeneratedAt] = useState('');
  const [responseRole, setResponseRole] = useState('');
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [capabilities, setCapabilities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const localizeInsight = useCallback((insight) => ({
    ...insight,
    title: insight.titleKey
      ? t(insight.titleKey, { defaultValue: insight.title })
      : insight.title,
    summary: insight.summaryKey
      ? t(insight.summaryKey, {
        ...(insight.summaryValues || {}),
        defaultValue: insight.summary,
      })
      : insight.summary,
  }), [t]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall('api/insights/feed');
      if (!response.ok) {
        throw new Error('load_failed');
      }
      const payload = await response.json();
      const normalized = normalizeInsightsResponse(payload);
      setInsights(normalized.insights.map(localizeInsight));
      setForecast(normalized.forecast);
      setGeneratedAt(normalized.generatedAt);
      setResponseRole(normalized.role);
      const capabilityResponse = await makeApiCall('api/insights/capabilities');
      if (capabilityResponse.ok) {
        const capabilityPayload = await capabilityResponse.json();
        setCapabilities(Object.fromEntries(
          (capabilityPayload.capabilities || []).map((capability) => [
            capability.capability_key,
            capability,
          ])
        ));
      }
    } catch {
      setError(t('insights.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [localizeInsight, t]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const role = String(responseRole || user?.user_type || 'employee').toLowerCase();
  const roleLabel = t(`roles.${role === 'crew_manager' ? 'crewManager' : role}`, {
    defaultValue: role.replace(/_/g, ' '),
  });
  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';

  const getFreshnessLabel = useCallback((dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const elapsedMinutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    if (elapsedMinutes < 2) return t('insights.justNow');
    if (elapsedMinutes < 60) return t('insights.minutesAgo', { count: elapsedMinutes });
    const hours = Math.floor(elapsedMinutes / 60);
    if (hours < 24) return t('insights.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t('insights.daysAgo', { count: days });
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  }, [locale, t]);

  const latestUpdate = useMemo(() => {
    const dates = [generatedAt, ...insights.map((insight) => insight.generatedAt)]
      .map((value) => new Date(value).getTime())
      .filter(Number.isFinite);
    return dates.length ? new Date(Math.max(...dates)).toISOString() : '';
  }, [generatedAt, insights]);

  const submitFeedback = async (insight, feedback) => {
    const response = await makeApiCall(`api/insights/feed/${encodeURIComponent(insight.id)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback_type: feedback }),
    });
    if (!response.ok) {
      throw new Error(t('insights.feedbackFailed'));
    }
    setInsights((current) => current.map((item) => (
      String(item.id) === String(insight.id) ? { ...item, feedback } : item
    )));
    setSelectedInsight((current) => current ? { ...current, feedback } : current);
  };

  const openInsight = async (insight) => {
    setSelectedInsight(insight);
    setDetailLoading(true);
    setDetailError('');
    try {
      const response = await makeApiCall(`api/insights/feed/${encodeURIComponent(insight.id)}`);
      if (!response.ok) throw new Error('detail_failed');
      const payload = await response.json();
      setSelectedInsight(localizeInsight(normalizeInsight(payload.insight || insight)));
    } catch {
      setDetailError(t('insights.detailLoadFailed'));
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box component="section" aria-labelledby="insights-center-title" sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeOutlined sx={{ color: '#1565c0' }} aria-hidden="true" />
            <Typography id="insights-center-title" variant={compact ? 'h6' : 'h5'} component="h2" sx={{ fontWeight: 700 }}>
              {t('insights.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('insights.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
          <Chip
            size="small"
            variant="outlined"
            label={t('insights.roleView', { role: roleLabel })}
            aria-label={t('insights.roleViewAria', { role: roleLabel })}
          />
          <Tooltip title={t('common.refresh')}>
            <span>
              <IconButton
                onClick={loadInsights}
                disabled={loading}
                aria-label={t('insights.refreshAria')}
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {latestUpdate && !loading && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('insights.lastUpdated', { freshness: getFreshnessLabel(latestUpdate) })}
        </Typography>
      )}

      {loading && (
        <Card variant="outlined" aria-live="polite">
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <CircularProgress size={32} aria-label={t('insights.loadingAria')} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t('insights.loading')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={loadInsights} aria-label={t('insights.retryAria')}>
              {t('insights.retry')}
            </Button>
          )}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && ['crew_manager', 'admin', 'superadmin'].includes(role) && (
        <MorningBriefPanel />
      )}

      {!loading && !error && !insights.length && !forecast.length && (
        <Card variant="outlined">
          <CardContent sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" component="h3">{t('insights.emptyTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {t('insights.emptyDescription')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && !error && forecast.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <AISeasonalChart data={forecast} generatedAt={generatedAt} />
        </Box>
      )}

      {!loading && !error && insights.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: compact
              ? '1fr'
              : { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {insights.map((insight) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              freshnessLabel={getFreshnessLabel(insight.generatedAt)}
              onOpen={openInsight}
            />
          ))}
        </Box>
      )}

      {role === 'superadmin' && (
        <Box sx={{ mt: 2 }}>
          <InsightsQualityPanel />
        </Box>
      )}

      {capabilities.agentic_copilot?.effective_enabled
        && ['crew_manager', 'sales', 'admin', 'superadmin'].includes(role) && (
        <Box sx={{ mt: 2 }}>
          <CopilotPanel />
        </Box>
      )}

      <InsightDetailDialog
        insight={selectedInsight}
        open={Boolean(selectedInsight)}
        loading={detailLoading}
        loadError={detailError}
        freshnessLabel={getFreshnessLabel(selectedInsight?.generatedAt)}
        onClose={() => {
          setSelectedInsight(null);
          setDetailError('');
        }}
        onFeedback={submitFeedback}
      />
    </Box>
  );
};

export default InsightsCenter;
